'use client'

import { useParams, useNavigate } from 'react-router-dom'
import { CalendarContainer } from '@/features/calendar/calendar-container'
import { useState, useEffect } from 'react'
import { getMeetupById } from '@/services/meetups'
import { differenceInDays, parseISO } from 'date-fns'
import Button from '@/components/ui/button'

// Define a proper type for the meetup data
interface Meetup {
  id: string
  title: string
  description?: string
  location?: string
  start_time: string
  end_time: string
  limit_time_range?: boolean
  daily_start_time?: string
  daily_end_time?: string
}

function MeetupCalendarPage() {
  const params = useParams<{ id: string }>()
  const navigate = useNavigate()
  const meetupId = params.id
  const [meetup, setMeetup] = useState<Meetup | null>(null)
  const [initialView, setInitialView] = useState('timeGridWeek')
  const [visibleDates, setVisibleDates] = useState<Date[] | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [slotMinTime, setSlotMinTime] = useState<string>('00:00:00')
  const [slotMaxTime, setSlotMaxTime] = useState<string>('24:00:00')
  // Track if data is fully processed and ready to render
  const [dataReady, setDataReady] = useState(false)
  // Add a key to force re-render the calendar when dates are loaded
  const [calendarKey, setCalendarKey] = useState(0)
  // Add a delayed render flag to ensure the calendar mounts with the correct dates
  const [shouldRenderCalendar, setShouldRenderCalendar] = useState(false)

  // Log component state for debugging Vercel issues
  console.log('[MEETUP CALENDAR] Rendering component, state:', {
    meetupId,
    isLoading,
    dataReady,
    hasVisibleDates: visibleDates ? true : undefined,
    visibleDatesLength: visibleDates?.length || 0,
    hasCalendarKey: calendarKey,
    shouldRenderCalendar,
  })

  // Improved date creation logic with UTC consistency and timezone offset compensation
  const createVisibleDates = (startDate: Date, endDate: Date): Date[] => {
    console.log('[MEETUP CALENDAR] Creating visible dates from:', {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    })

    const dates: Date[] = []

    // Add timezone offset compensation
    // This ensures that when FullCalendar applies its timezone handling, we'll get the correct dates
    const timezoneOffset = new Date().getTimezoneOffset() * 60 * 1000
    console.log('[MEETUP CALENDAR] Timezone offset in milliseconds:', timezoneOffset)

    // Create dates with consistent UTC midnight time, adjusting for timezone offset
    const startUTC = new Date(
      Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 0, 0, 0, 0) +
        timezoneOffset // Add offset to compensate for FullCalendar's handling
    )

    const endUTC = new Date(
      Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 0, 0, 0, 0) +
        timezoneOffset // Add offset to compensate for FullCalendar's handling
    )

    // Log normalized dates for debugging
    console.log('[MEETUP CALENDAR] Normalized UTC dates with timezone compensation:', {
      startUTC: startUTC.toISOString(),
      endUTC: endUTC.toISOString(),
      timezoneOffset: timezoneOffset,
    })

    // Calculate days including both start and end date (inclusive range)
    const daysDifference = Math.round(
      (endUTC.getTime() - startUTC.getTime()) / (24 * 60 * 60 * 1000)
    )
    console.log('[MEETUP CALENDAR] Days difference (inclusive):', daysDifference + 1)

    // If startDate and endDate are the same day, just add that day
    if (daysDifference === 0) {
      dates.push(new Date(startUTC))
      console.log('[MEETUP CALENDAR] Single day meetup, added date:', dates[0].toISOString())
      return dates
    }

    // Add all dates from start to end (inclusive)
    let currentDate = new Date(startUTC)

    // Ensure we don't get into an infinite loop by limiting to 31 days max
    const maxDays = Math.min(daysDifference + 1, 31)
    console.log(`[MEETUP CALENDAR] Will create ${maxDays} dates`)

    for (let i = 0; i < maxDays; i++) {
      // Create a new date object for each day to avoid reference issues
      const dateToAdd = new Date(currentDate)
      dates.push(dateToAdd)
      console.log(`[MEETUP CALENDAR] Added date ${i + 1}/${maxDays}:`, dateToAdd.toISOString())

      // Move to next day (in UTC)
      currentDate.setUTCDate(currentDate.getUTCDate() + 1)
    }

    console.log(
      `[MEETUP CALENDAR] Created ${dates.length} visible dates:`,
      dates.map(d => d.toISOString())
    )

    return dates
  }

  useEffect(() => {
    async function fetchMeetupData() {
      if (!meetupId) return

      setIsLoading(true)
      setDataReady(false)
      setError(null)

      try {
        console.log('[MEETUP CALENDAR] Fetching meetup data for ID:', meetupId)
        const { data, error } = await getMeetupById(meetupId)

        if (error) throw new Error(error)
        if (!data) throw new Error('Meetup not found')

        console.log('[MEETUP CALENDAR] Meetup data received:', data)
        setMeetup(data)

        // Parse dates consistently without timezone adjustments
        // This ensures we get exactly the dates stored in the database
        console.log('[MEETUP CALENDAR] Raw date strings from DB:', {
          start: data.start_time,
          end: data.end_time,
        })

        // Parse ISO dates - using parseISO preserves the exact date information
        const startDate = parseISO(data.start_time)
        const endDate = parseISO(data.end_time)

        console.log('[MEETUP CALENDAR] Parsed dates (before any adjustments):', {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          startDateLocal: startDate.toString(),
          endDateLocal: endDate.toString(),
          startYear: startDate.getFullYear(),
          startMonth: startDate.getMonth(),
          startDay: startDate.getDate(),
          endYear: endDate.getFullYear(),
          endMonth: endDate.getMonth(),
          endDay: endDate.getDate(),
        })

        // Set time limits
        if (data.limit_time_range && data.daily_start_time && data.daily_end_time) {
          // Use the daily start and end times directly
          const formattedStartTime = data.daily_start_time.includes(':00')
            ? data.daily_start_time
            : `${data.daily_start_time}:00`

          const formattedEndTime = data.daily_end_time.includes(':00')
            ? data.daily_end_time
            : `${data.daily_end_time}:00`

          setSlotMinTime(formattedStartTime)
          setSlotMaxTime(formattedEndTime)
        } else {
          // Default time limits
          setSlotMinTime('00:00:00')
          setSlotMaxTime('24:00:00')
        }

        // Always use timeGridWeek for meetup calendars
        setInitialView('timeGridWeek')

        // Create array of dates for the meetup
        console.log('[MEETUP CALENDAR] Calling createVisibleDates')
        const dates = createVisibleDates(startDate, endDate)
        if (dates.length === 0) {
          throw new Error('Unable to create valid date range for meetup')
        }

        console.log(
          '[MEETUP CALENDAR] Final visible dates array:',
          dates.map(d => d.toISOString()),
          'Length:',
          dates.length
        )
        setVisibleDates(dates)

        // Final step: Set calendar ready state and increase the calendar key to force re-render
        console.log('[MEETUP CALENDAR] Setting dataReady=true')
        setDataReady(true)

        // Increment the calendar key to force a re-render with the correct dates
        setCalendarKey(prev => prev + 1)

        // Delayed render to ensure the calendar mounts after all data is processed
        setTimeout(() => {
          setShouldRenderCalendar(true)
        }, 100)
      } catch (err) {
        console.error('[MEETUP CALENDAR] Error fetching meetup details:', err)
        setError(err instanceof Error ? err.message : 'Failed to load meetup details')
        setIsLoading(false)
        setDataReady(false)
      }
    }

    if (meetupId) {
      fetchMeetupData()
    }
  }, [meetupId])

  // Handle back to meetup button
  const handleBackClick = () => {
    navigate(`/meetups/${meetupId}`)
  }

  if (isLoading && !dataReady) {
    return <div className="p-8 text-center">Loading calendar...</div>
  }

  if (error) {
    return <div className="p-8 text-center text-red-500">{error}</div>
  }

  return (
    <div className="py-4 mx-auto max-w-7xl">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-bold">
          <span className="text-primary">{meetup?.title}</span> Schedule
        </h1>
        <Button onClick={handleBackClick} variant="outline">
          Back to Meetup
        </Button>
      </div>

      {dataReady && shouldRenderCalendar ? (
        <CalendarContainer
          key={calendarKey}
          meetupId={meetupId}
          initialView={initialView}
          visibleDates={visibleDates}
          calendarTitle={meetup?.title ? `${meetup.title} Calendar` : 'Meetup Calendar'}
          slotMinTime={slotMinTime}
          slotMaxTime={slotMaxTime}
          location={meetup?.location}
          hideHeader={true}
        />
      ) : (
        <div className="p-8 text-center">Preparing calendar view...</div>
      )}
    </div>
  )
}

export default MeetupCalendarPage
